import {
  IconBuildingSkyscraper,
  IconCalendar,
  IconHeart,
  IconHeartFilled,
  IconPhotoFilled,
  IconPlane,
} from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "src/components/UI/Button/Button";
import { OverlayLink } from "src/components/UI/OverlayLink/OverlayLink";
import { useLocaleContext } from "src/context/locale-context";
import { favoritesActions, selectIsFavorited } from "src/store/favorites";
import { doMoneyMultiplication } from "src/utils/money-utils";
import { IMG_SIZES } from "src/pages/loaders/UserTripsLoader";
import { getAvioText } from "./avioCeljojumiText";
import classes from "./AvioHotelCard.module.css";

// Real hotel data (same shape/source as "Lēti viesnīcu piedāvājumi"), presented as a flight+hotel
// PACKAGE card: destination is the primary title, hotel name is secondary — the opposite
// hierarchy from a plain hotel listing card. No flight number/airline/nights field exists on
// this data, so the card only ever shows the generic "Lidojums + viesnīca" label, never a
// fabricated flight detail (see project rule: don't invent data that isn't in the API).
export function AvioHotelCard({ item }) {
  const { getStringRes, currencySymbol } = useLocaleContext();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [imgError, setImgError] = useState(false);
  const favoriteId = `hotel-${item.id}`;
  const isFavorite = useSelector((state) => selectIsFavorited(state, favoriteId));

  const guestCount = Array.isArray(item.guests)
    ? item.guests.reduce((sum, count) => sum + Number(count || 0), 0)
    : parseInt(item.guests) || 1;
  const rating = Number(item.rating || 0);
  const hasRating = rating > 0;
  const pricePerPerson =
    item.totalPrice && guestCount
      ? doMoneyMultiplication(parseFloat(item.totalPrice), 1 / guestCount)
      : null;

  const link = item.link ? (item.link.startsWith("/") ? item.link : `/${item.link}`) : null;
  const hasImage = !imgError && !!item.image;
  const imageSrc = hasImage ? item.image.replace("{size}", IMG_SIZES.medium) : null;
  const destination = item.city || item.name;

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(favoritesActions.toggleFavorite({ id: favoriteId, type: "hotel", data: item }));
    if (!isFavorite) {
      toast.success(getStringRes("favorites.toast.saved"));
    }
  };

  const onViewDeal = (e) => {
    e.preventDefault();
    if (link) navigate(link);
  };

  const cardContent = (
    <>
      <div className={classes.imageWrap}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={destination}
            className={classes.image}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={classes.imgFallback}>
            <IconPhotoFilled size="35%" color="var(--ui-4)" />
          </div>
        )}
        <span className={classes.typeBadge}>
          <IconPlane size={14} stroke={1.75} />
          <span aria-hidden="true">+</span>
          <IconBuildingSkyscraper size={14} stroke={1.75} />
        </span>
        {!!item.discount && (
          <span className={classes.discountBadge}>
            {getStringRes("cheap.hotels.discount.badge").replace("##DISCOUNT##", item.discount)}
          </span>
        )}
      </div>

      <div className={classes.body}>
        <h3 className={classes.destination}>{destination}</h3>
        <p className={classes.packageLabel}>
          <IconPlane size={13} stroke={1.75} />
          <IconBuildingSkyscraper size={13} stroke={1.75} />
          {getAvioText(getStringRes, "avio.deals.badge")}
        </p>

        {item.dates && (
          <div className={classes.metaRow}>
            <IconCalendar size={15} stroke={1.75} />
            <span>{item.dates}</span>
          </div>
        )}

        <div className={classes.hotelRow}>
          <span className={classes.hotelName}>{item.name}</span>
          {hasRating ? (
            // `rating` is a 0–10 guest review score, not a 1–5 star class — rendering it as
            // Math.round(rating) stars used to overflow to "all 5 filled" for any score ≥5.
            <span className={`${classes.ratingChip} ${rating >= 8 ? classes.ratingGood : ""}`}>
              {rating.toFixed(1)}
            </span>
          ) : (
            <span className={classes.ratingNone}>
              {getAvioText(getStringRes, "cheap.hotels.rating.none")}
            </span>
          )}
        </div>

        <hr className={classes.divider} />

        <div className={classes.priceRow}>
          {pricePerPerson ? (
            <>
              <span className={classes.priceLabel}>
                {getAvioText(getStringRes, "avio.deals.priceFrom")}
              </span>
              <div className={classes.priceLine}>
                <span className={classes.priceAmount}>
                  {pricePerPerson.toFixed(2)} {currencySymbol}
                </span>
                <span className={classes.pricePerPerson}>
                  {getStringRes("cheap.hotels.price.per.person")}
                </span>
              </div>
            </>
          ) : (
            <span className={classes.priceNone}>{getStringRes("cheap.hotels.price.none")}</span>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={classes.card}>
      {link ? (
        <OverlayLink className={classes.cardLink} to={link}>
          {cardContent}
        </OverlayLink>
      ) : (
        <div className={classes.cardLink}>{cardContent}</div>
      )}

      <button
        type="button"
        className={classes.favoriteButton}
        aria-label={getStringRes("cheap.hotels.favorite")}
        onClick={onToggleFavorite}>
        {isFavorite ? (
          <IconHeartFilled size={16} color="#ff2965" />
        ) : (
          <IconHeart size={16} stroke={1.75} />
        )}
      </button>

      <div className={classes.ctaWrap}>
        <Button
          name={
            <>
              {getAvioText(getStringRes, "avio.deals.view")} <span aria-hidden>→</span>
            </>
          }
          primary
          wide
          disabled={!link}
          onClick={onViewDeal}
        />
      </div>
    </div>
  );
}
